import axios from "axios";

export const apiChannels = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    Authorization: "Bearer " + process.env.REACT_APP_API_KEY,
  },
});

export const listData = async (args) => {
  try {
    const params = new URLSearchParams();
    if (args.term !== undefined) params.append("term", args.term ?? "");
    if (args.value !== undefined) params.append("value", args.value ?? "");
    if (args.limit !== undefined) params.append("limit", String(args.limit));
    if (args.cursor !== undefined) params.append("cursor", args.cursor ?? "");
    if (args.status) params.append("status", args.status);
    if (args.statusNot) params.append("statusNot", args.statusNot);
    if (args.dateFrom) params.append("dateFrom", args.dateFrom);
    if (args.dateTo) params.append("dateTo", args.dateTo);
    if (args.filterName) params.append("filterName", args.filterName);
    if (args.filterEmail) params.append("filterEmail", args.filterEmail);
    if (args.motivationCode) params.append("motivationCode", args.motivationCode);

    const response = await apiChannels.get(`${args.table}?${params}`);
    return response;
  } catch (error) {
    return new Error(`List Data from ${args.table} Error: ${error.message}`);
  }
};

export const checkDonationStatus = async ({ transactionId, orderId, donationId }) => {
  try {
    const params = new URLSearchParams();
    if (transactionId) params.append("transaction_id", transactionId);
    if (orderId) params.append("order_id", orderId);
    if (donationId) params.append("donationId", donationId);

    const response = await apiChannels.get(`donations/check-status?${params}`);
    return response;
  } catch (error) {
    return new Error(`Check Donation Status Error: ${error.message}`);
  }
};

export const checkPendingDonations = async () => {
  try {
    const response = await apiChannels.get("donations/check-pending");
    return response;
  } catch (error) {
    return new Error(`Check Pending Donations Error: ${error.message}`);
  }
};

export const getDonationsSummary = async ({
  dateFrom,
  dateTo,
  channelId,
  motivationCode,
}) => {
  try {
    const params = new URLSearchParams();
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);
    if (channelId) params.append("channelId", channelId);
    if (motivationCode) params.append("motivationCode", motivationCode);

    const response = await apiChannels.get(`donations/summary?${params}`);
    return response;
  } catch (error) {
    return new Error(`Donations Summary Error: ${error.message}`);
  }
};

export const getDonationsSummaryByCode = async ({
  dateFrom,
  dateTo,
  channelId,
  motivationCode,
}) => {
  try {
    const params = new URLSearchParams();
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);
    if (channelId) params.append("channelId", channelId);
    if (motivationCode) params.append("motivationCode", motivationCode);

    const response = await apiChannels.get(`donations/summary-by-code?${params}`);
    return response;
  } catch (error) {
    return new Error(`Donations Summary By Code Error: ${error.message}`);
  }
};

export const getDonationsSummaryByCountry = async ({
  dateFrom,
  dateTo,
  channelId,
  motivationCode,
}) => {
  try {
    const params = new URLSearchParams();
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);
    if (channelId) params.append("channelId", channelId);
    if (motivationCode) params.append("motivationCode", motivationCode);

    const response = await apiChannels.get(
      `donations/summary-by-country?${params}`
    );
    return response;
  } catch (error) {
    return new Error(`Donations Summary By Country Error: ${error.message}`);
  }
};

export const listAuthUsers = async () => {
  try {
    const response = await apiChannels.get("auth/users");
    return response;
  } catch (error) {
    return new Error(`List Users Error: ${error.message}`);
  }
};

export const createAuthUser = async ({ email, password, name }) => {
  try {
    const response = await apiChannels.post("auth/users", {
      email,
      password,
      name,
    });
    return response;
  } catch (error) {
    return new Error(`Create User Error: ${error.message}`);
  }
};

export const updateAuthUser = async (email, { name, password }) => {
  try {
    const response = await apiChannels.put(
      `auth/users/${encodeURIComponent(email)}`,
      {
        name,
        password,
      }
    );
    return response;
  } catch (error) {
    return new Error(`Update User Error: ${error.message}`);
  }
};

export const deleteAuthUser = async (email) => {
  try {
    const response = await apiChannels.delete(
      `auth/users/${encodeURIComponent(email)}`
    );
    return response;
  } catch (error) {
    return new Error(`Delete User Error: ${error.message}`);
  }
};

export const filterByField = async (table, field, value) => {
  try {
    if (!field) {
      field = "";
    }
    if (!value) {
      value = "";
    }
    const response = await apiChannels.get(
      `${table}?term=${field}&value=${value}`
    );

    return response;
  } catch (error) {
    return new Error("Filter By Field Error: ", error.message);
  }
};

export const createMotivationCode = async (payload) => {
  try {
    const response = await apiChannels.post("motivation-codes", payload);
    return response;
  } catch (error) {
    return new Error(`Create Motivation Code Error: ${error.message}`);
  }
};

export const updateMotivationCode = async (payload) => {
  try {
    const response = await apiChannels.put("motivation-codes", payload);
    return response;
  } catch (error) {
    return new Error(`Update Motivation Code Error: ${error.message}`);
  }
};

export const deleteMotivationCode = async (motivationCode) => {
  try {
    const response = await apiChannels.delete(
      `motivation-codes?motivationCode=${encodeURIComponent(motivationCode)}`
    );
    return response;
  } catch (error) {
    return new Error(`Delete Motivation Code Error: ${error.message}`);
  }
};

export const getMotivationCodeContent = async (motivationCode) => {
  try {
    const response = await apiChannels.get(
      `motivation-codes/content/${encodeURIComponent(motivationCode)}`
    );
    return response;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return { data: null };
    }
    return new Error(`Get Motivation Code Content Error: ${error.message}`);
  }
};

export const saveMotivationCodeContent = async (payload) => {
  try {
    const response = await apiChannels.post("motivation-codes/content", payload);
    return response;
  } catch (error) {
    return new Error(`Save Motivation Code Content Error: ${error.message}`);
  }
};

export const deleteMotivationCodeContent = async (motivationCode) => {
  try {
    const response = await apiChannels.delete(
      `motivation-codes/content?motivationCode=${encodeURIComponent(
        motivationCode
      )}`
    );
    return response;
  } catch (error) {
    return new Error(`Delete Motivation Code Content Error: ${error.message}`);
  }
};

export const uploadPuckImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append("image", file);
    const response = await apiChannels.post(
      "motivation-codes/content/upload-image",
      formData,
    );
    return response.data;
  } catch (error) {
    const detail =
      error?.response?.data?.error ||
      error?.response?.data ||
      error?.message ||
      "unknown error";
    throw new Error(`Upload Puck Image Error: ${detail}`);
  }
};

export const sendToSalesforce = async (subscriptionId) => {
  try {
    const response = await apiChannels.post(
      `subscriptions/${subscriptionId}/syncToSF`
    );

    return response.data;
  } catch (error) {
    throw new Error("Sending on Salesforce endpoint", error);
  }
};

export const markSubscriptionResolved = async (subscriptionId) => {
  try {
    const response = await apiChannels.post(
      `subscriptions/${subscriptionId}/mark-resolved`
    );
    return response;
  } catch (error) {
    return new Error(`Mark Subscription Resolved Error: ${error.message}`);
  }
};
